import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Link,
  IconButton,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { SectionVariable } from '../types';
import { fetchAllFAQs, FAQItemDisplay } from '../api/faq';
import { useSettings } from '../Settings';
import { labelTagSx } from '../theme/tokens';
import { SlideUpModal } from '../theme/shared';
import { useTranslation } from '../i18n';

interface FAQProps {
  variables?: SectionVariable;
  name?: string;
}

export function FAQ({ variables: _variables }: FAQProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { settings } = useSettings();
  const { t } = useTranslation();
  const [faqs, setFaqs] = useState<FAQItemDisplay[]>(t.faq.fallback);
  const [openModal, setOpenModal] = useState(false);
  const [selectedFAQ, setSelectedFAQ] = useState<FAQItemDisplay | null>(null);

  // Refresh fallback when language changes (only relevant when middleware
  // does not return any FAQs — otherwise the middleware payload wins).
  useEffect(() => {
    setFaqs((current) => (current === t.faq.fallback || current.length === 0 ? t.faq.fallback : current));
  }, [t]);

  useEffect(() => {
    if (!settings.middlewareUrl) return;

    let cancelled = false;

    async function loadFaqs() {
      const data = await fetchAllFAQs(settings.middlewareUrl!);
      if (cancelled) return;
      if (data && data.length > 0) {
        setFaqs(data);
      }
    }
    loadFaqs();

    return () => { cancelled = true; };
  }, [settings.middlewareUrl]);

  const scrollLeft = () => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: -320, behavior: 'smooth' });
  };
  const scrollRight = () => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: 320, behavior: 'smooth' });
  };

  const handleOpen = (item: FAQItemDisplay) => {
    setSelectedFAQ(item);
    setOpenModal(true);
  };

  const handleClose = () => {
    setOpenModal(false);
  };

  return (
    <Box
      className="faq"
      sx={{
        py: { xs: 8, md: 10 },
        bgcolor: '#FAFAFA', // Subtle contrast just to isolate the section
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 4 },
          maxWidth: 1280,
          mx: 'auto',
        }}
      >
        <Grid container spacing={4} alignItems="center">
          {/* Left - Titles and description */}
          <Grid size={{ xs: 12, md: 3.5 }}>
            <Box
              sx={{
                ...labelTagSx,
                display: 'inline-block',
                mb: 3
              }}
            >
              {t.faq.tag}
            </Box>

            <Typography
              sx={{
                color: '#585858',
                fontFamily: "'Figtree', sans-serif",
                fontSize: { xs: '1.75rem', md: '2rem' },
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: '115.065%',
                mb: 2
              }}
            >
              {t.faq.titleLine1}
              <br />
              {t.faq.titleLine2}
            </Typography>

            <Typography
              sx={{
                color: '#585858',
                fontFamily: "'Figtree', sans-serif",
                fontSize: '0.875rem',
                fontWeight: 400,
                mb: 4
              }}
            >
              {t.faq.subtitle}
            </Typography>

            <Box display={{ xs: 'none', md: 'flex' }} gap={1}>
              <IconButton 
                onClick={scrollLeft} 
                sx={{ 
                  bgcolor: '#FFF', 
                  boxShadow: '0 2px 8px #0000000D', 
                  '&:hover': { bgcolor: '#F0F0F0' } 
                }}
              >
                <ArrowBackIosNewIcon fontSize="small" />
              </IconButton>
              <IconButton 
                onClick={scrollRight} 
                sx={{ 
                  bgcolor: '#FFF', 
                  boxShadow: '0 2px 8px #0000000D', 
                  '&:hover': { bgcolor: '#F0F0F0' } 
                }}
              >
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
            </Box>
          </Grid>

          {/* Right - Carousel FAQ cards */}
          <Grid size={{ xs: 12, md: 8.5 }}>
            <Box
              ref={scrollRef}
              sx={{
                display: 'flex',
                gap: 3,
                overflowX: 'auto',
                pb: 3, // Room for shadow/scroll
                scrollSnapType: 'x mandatory',
                scrollbarWidth: 'none', // Firefox
                '&::-webkit-scrollbar': {
                  display: 'none', // Safari and Chrome
                },
              }}
            >
              {faqs.map((item, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    minWidth: { xs: '260px', md: '280px' },
                    minHeight: '300px', // Maior em relacao aos outros
                    borderRadius: '24px', // Raio arredondado como cartoes no Print 3
                    border: '1px solid #EAEAEA',
                    bgcolor: '#FFF',
                    p: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    scrollSnapAlign: 'start',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 24px #0000000A',
                    }
                  }}
                >
                  <Box
                    sx={{
                      ...labelTagSx,
                      width: 'fit-content',
                    }}
                  >
                    {item.category}
                  </Box>

                  <Typography
                    sx={{
                      color: '#585858',
                      fontFamily: "'Figtree', sans-serif",
                      fontSize: '1.25rem',
                      fontWeight: 600,
                      lineHeight: 1.3,
                      mt: 4,
                      mb: 4,
                    }}
                  >
                    {item.question}
                  </Typography>

                  <Link
                    component="button"
                    onClick={() => handleOpen(item)}
                    underline="hover"
                    sx={{
                      color: '#5F5F5F',
                      fontFamily: "'Figtree', sans-serif",
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      mt: 'auto',
                      textAlign: 'left',
                      width: 'fit-content'
                    }}
                  >
                    {t.faq.viewAnswer}
                  </Link>
                </Paper>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <SlideUpModal
        open={openModal}
        onClose={handleClose}
        tag={selectedFAQ?.category || 'FAQ'}
        title={selectedFAQ?.question || ''}
      >
        <Typography
          sx={{
            color: '#585858',
            fontFamily: "'Figtree', sans-serif",
            fontSize: '1rem',
            lineHeight: 1.6,
          }}
        >
          {selectedFAQ?.answer}
        </Typography>
      </SlideUpModal>
    </Box>
  );
}
